import { useState } from 'react'
import {
  History,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useHistory, useHistoryVersion, useRevertEntity } from '@/hooks'
import { ConfirmDialog } from '@/components'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import type { HistoryEntry, FieldChange } from '@/types'

export interface HistoryTimelineProps {
  entityType: string
  entityId: string
  createdAt: string
  updatedAt: string
  updatedBy?: string
  updatedByName?: string
  canRevert?: boolean
}

export function HistoryTimeline({
  entityType,
  entityId,
  createdAt,
  updatedAt,
  updatedBy,
  updatedByName,
  canRevert: canRevertProp,
}: HistoryTimelineProps) {
  const { user } = useAuth()
  const { data: historyData, isLoading: historyLoading } = useHistory(
    entityType,
    entityId
  )
  const revertMutation = useRevertEntity(entityType, entityId)

  const [revertEntry, setRevertEntry] = useState<HistoryEntry | null>(null)
  const [expandedTimestamp, setExpandedTimestamp] = useState<number | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const canRevert =
    canRevertProp ?? (user?.role === 'super_admin' || user?.role === 'admin')

  return (
    <>
      <Card className="print:hidden">
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setIsOpen(!isOpen)}
        >
          <CardTitle className="text-lg flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
            <History className="h-5 w-5" />
            History
            {historyData?.items && historyData.items.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {historyData.items.length}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        {isOpen && (
          <CardContent>
            <dl className="space-y-2 text-sm mb-4">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Created</dt>
                <dd>{new Date(createdAt).toLocaleString()}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Last updated</dt>
                <dd>{new Date(updatedAt).toLocaleString()}</dd>
              </div>
              {updatedBy && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Updated by</dt>
                  <dd>{updatedByName || updatedBy}</dd>
                </div>
              )}
            </dl>

            {historyLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading history...
              </div>
            ) : historyData?.items && historyData.items.length > 0 ? (
              <>
                <Separator className="mb-4" />
                <div className="space-y-3">
                  {historyData.items.map((entry) => {
                    const date = new Date(entry.changed_at)
                    const isExpanded = expandedTimestamp === entry.timestamp
                    return (
                      <div
                        key={entry.timestamp}
                        className="relative border rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-start gap-3 min-w-0">
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedTimestamp(
                                  isExpanded ? null : entry.timestamp
                                )
                              }
                              className="mt-0.5 text-muted-foreground hover:text-foreground"
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <ChangeTypeBadge type={entry.change_type} />
                                <span className="text-sm text-muted-foreground">
                                  by {entry.changed_by_name || entry.changed_by}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {date.toLocaleDateString()}{' '}
                                {date.toLocaleTimeString()}
                              </p>
                              {entry.change_reason && (
                                <p className="text-sm mt-1 text-muted-foreground italic">
                                  {entry.change_reason}
                                </p>
                              )}
                              {entry.changes && entry.changes.length > 0 && (
                                <FieldChanges changes={entry.changes} />
                              )}
                            </div>
                          </div>
                          {canRevert && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevertEntry(entry)}
                              className="shrink-0"
                            >
                              <RotateCcw className="mr-1 h-3 w-3" />
                              Revert
                            </Button>
                          )}
                        </div>
                        {isExpanded && (
                          <SnapshotViewer
                            entityType={entityType}
                            entityId={entityId}
                            timestamp={entry.timestamp}
                          />
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            ) : null}
          </CardContent>
        )}
      </Card>

      <ConfirmDialog
        open={!!revertEntry}
        onOpenChange={(open) => !open && setRevertEntry(null)}
        title={`Revert ${fieldLabel(entityType.replace(/s$/, ''))}`}
        description={`Are you sure you want to revert to the version from ${revertEntry ? new Date(revertEntry.changed_at).toLocaleString() : ''}? This will create a new version with the previous data.`}
        confirmText="Revert"
        onConfirm={() => {
          if (revertEntry) {
            revertMutation.mutate(
              { version: revertEntry.timestamp },
              { onSuccess: () => setRevertEntry(null) }
            )
          }
        }}
        isLoading={revertMutation.isPending}
      />
    </>
  )
}

const SNAPSHOT_SKIP_FIELDS = new Set([
  'id',
  'org_id',
  'pk',
  'sk',
  'gsi1_pk',
  'gsi1_sk',
  'created_by',
  'updated_by',
  'deleted_at',
])

function SnapshotViewer({
  entityType,
  entityId,
  timestamp,
}: {
  entityType: string
  entityId: string
  timestamp: number
}) {
  const { data, isLoading, error } = useHistoryVersion(
    entityType,
    entityId,
    timestamp
  )

  if (isLoading) {
    return (
      <div className="mt-3 ml-7 flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading snapshot...
      </div>
    )
  }

  if (error || !data?.snapshot) {
    return (
      <div className="mt-3 ml-7 text-xs text-muted-foreground">
        Unable to load snapshot.
      </div>
    )
  }

  const entries = Object.entries(data.snapshot).filter(
    ([key]) => !SNAPSHOT_SKIP_FIELDS.has(key)
  )

  if (entries.length === 0) {
    return (
      <div className="mt-3 ml-7 text-xs text-muted-foreground">
        No displayable fields in this snapshot.
      </div>
    )
  }

  return (
    <div className="mt-3 ml-7 text-xs bg-muted/50 rounded p-3 overflow-auto max-h-64">
      <p className="mb-2 font-medium text-foreground">
        Snapshot at this version:
      </p>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-muted-foreground font-medium whitespace-nowrap">
              {fieldLabel(key)}
            </dt>
            <dd className="text-foreground break-words">
              {formatSnapshotValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}

function formatSnapshotValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    // Check if it looks like an ISO date
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      return new Date(value).toLocaleString()
    }
    return value
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    return JSON.stringify(value, null, 2)
  }
  return JSON.stringify(value, null, 2)
}

function ChangeTypeBadge({ type }: { type: string }) {
  switch (type) {
    case 'update':
      return <Badge variant="secondary">Updated</Badge>
    case 'delete':
      return <Badge variant="destructive">Deleted</Badge>
    case 'revert':
      return <Badge variant="outline">Reverted</Badge>
    default:
      return <Badge variant="secondary">{type}</Badge>
  }
}

function FieldChanges({ changes }: { changes: FieldChange[] }) {
  return (
    <div className="mt-2 space-y-1">
      {changes.map((change) => (
        <div key={change.field} className="text-xs">
          <span className="font-medium text-foreground">
            {fieldLabel(change.field)}:
          </span>{' '}
          <span className="text-muted-foreground">
            {change.old ? (
              <>
                &quot;{truncate(change.old, 60)}&quot;{' '}
              </>
            ) : (
              <span className="italic">empty</span>
            )}
          </span>
          <span className="text-muted-foreground mx-1">&rarr;</span>
          <span className="text-foreground">
            {change.new ? (
              <>&quot;{truncate(change.new, 60)}&quot;</>
            ) : (
              <span className="italic">empty</span>
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

function fieldLabel(field: string): string {
  return field
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function truncate(str: string, max: number): string {
  if (str.length <= max) return str
  return str.slice(0, max) + '...'
}
